import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const IMG = "https://images.unsplash.com/photo-1775257796019-3e8db981a1a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwY29uY2llcmdlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg1OTE5MDQwfDA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: params.get("role") === "partner" ? "partner" : "member",
    referral_code: params.get("ref") || "",
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const plan = params.get("plan");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Bienvenido a SolClub");
      if (u.role === "partner") navigate("/partner");
      else navigate(plan ? "/dashboard?plan=" + plan : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/20 py-4 text-white placeholder-white/30 font-light focus:border-gold focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505] text-white">
      <div className="flex flex-col justify-center px-8 lg:px-24 py-16 order-2 lg:order-1">
        <Link to="/" className="flex items-center gap-2 text-white/50 hover:text-gold text-xs tracking-luxe-sm uppercase mb-16" data-testid="back-home">
          <ArrowLeft size={16} strokeWidth={1.5} /> Inicio
        </Link>
        <p className="text-xs tracking-luxe uppercase text-gold mb-4">Únase a SolClub</p>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-10">Cree su cuenta</h1>

        <div className="flex gap-3 mb-10 max-w-sm" data-testid="role-toggle">
          {[
            { v: "member", l: "Miembro" },
            { v: "partner", l: "Partner" },
          ].map((r) => (
            <button
              key={r.v}
              type="button"
              data-testid={`role-${r.v}`}
              onClick={() => setForm({ ...form, role: r.v })}
              className={`flex-1 py-3 text-xs tracking-luxe-sm uppercase border transition-colors ${
                form.role === r.v ? "border-gold text-gold" : "border-white/20 text-white/50 hover:border-white/40"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-8 max-w-sm" data-testid="register-form">
          <input required data-testid="register-name" className={field} placeholder="Nombre completo"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input required type="email" data-testid="register-email" className={field} placeholder="Email"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input required type="password" minLength={6} data-testid="register-password" className={field} placeholder="Contraseña (mín. 6 caracteres)"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {form.role === "partner" && (
            <input data-testid="register-referral" className={field} placeholder="Código de referido (opcional)"
              value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value })} />
          )}
          <button type="submit" disabled={loading} data-testid="register-submit"
            className="w-full bg-gold text-black py-4 text-xs tracking-luxe uppercase hover:bg-[#C5A059] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loading && <Loader2 size={16} className="animate-spin" />} Crear Cuenta
          </button>
        </form>
        <p className="text-white/50 font-light text-sm mt-10">
          ¿Ya es miembro?{" "}
          <Link to="/login" className="text-gold gold-underline" data-testid="go-login">Acceder</Link>
        </p>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={IMG} alt="Concierge" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/40" />
      </div>
    </div>
  );
}
